<template>
    <div v-show="can.create.bookable_booking">
        <button-primary @click="open">
            <i class="fa fa fa-calendar-plus-o"></i> {{ category.name }} buchen
        </button-primary>
        <portal to="modal" v-if="showModal">
            <modal @close="showModal = false">
                <template v-slot:header>Buchung erstellen</template>
                <div class="mb-4">
                    <t-rich-select
                        :fetch-options="getUsers"
                        placeholder="Benutzer Suchen"
                        value-attribute="id"
                        text-attribute="full_name"
                        :minimum-input-length="1"
                        minimumInputLengthText="Bitte geben Sie ein oder mehrere Zeichen ein."
                        v-model="userId"
                        searchBoxPlaceholder="Suchen..."
                        noResultsText="Keine Treffer"
                        searchingText="Suche..."
                    >
                    </t-rich-select>
                </div>
                <div class="grid grid-cols-12 gap-6 mb-16">
                    <select-menu class="col-start-8 col-span-4" v-model="timeSelectionMode"
                                 :values="['free', 'slot', 'multi-day']" :text="['Frei', 'Slot', 'mehrere Tage']">
                        Zeitmodus
                    </select-menu>

                    <date-time-range-select v-model="range"
                                            :time-selection-mode="timeSelectionMode"
                    ></date-time-range-select>

                    <input-text v-model="comment" class="mb-4 col-span-12">
                        Notiz
                    </input-text>

                    <toggle-group class="col-span-12" v-show="timeSelectionMode === 'multi-day'"
                                  v-model="hasOneBookingPerDay">
                        Eine Buchung pro Tag
                        <template v-slot:helper>
                            Hilfreich, wenn man eine Resource auf einen Mitarbeiter bucht und dieser einzelne Tage die
                            Resource an einzelnen Tagen zur Verfügung stellen darf.
                        </template>
                    </toggle-group>

                    <div v-if="selectedItem"
                         class="mb-4 col-span-12 sm:col-span-6">
                        <div class="text-sm font-medium leading-5 text-gray-700 sm:mt-px sm:pt-2">
                            {{ category.name }}
                        </div>
                        <div>
                            <a @click="selectedItem = null"
                               class="cursor-pointer inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium leading-4 bg-office-primary text-white"
                            >
                                {{ selectedItem.name }}
                                <button type="button"
                                        class="flex-shrink-0 ml-1.5 inline-flex text-white focus:outline-none focus:text-white"
                                        aria-label="">
                                    <svg class="h-2 w-2" stroke="currentColor" fill="none" viewBox="0 0 8 8">
                                        <path stroke-linecap="round" stroke-width="1.5" d="M1 1l6 6m0-6L1 7"/>
                                    </svg>
                                </button>
                            </a>
                        </div>
                    </div>
                </div>

                <div>
                    <div v-show="page === 'overview'">

                        <loading v-show="loading"></loading>

                        <tailwind-table v-show="items.length > 0 " class="mt-8" :header="[category.name, '']">
                            <tr v-for="(item, i) in items" :class="{'bg-gray-50': i % 2}">
                                <td class="px-6 py-3">{{ item.name }}</td>
                                <td class="px-6 py-3 text-right">
                                    <button-white @click="selectedItem = item">Auswählen</button-white>
                                </td>
                            </tr>
                        </tailwind-table>

                        <p v-show="items.length === 0">Keine buchbaren {{ category.name }} gefunden.</p>

                    </div>

                </div>

                <div v-if="page === 'confirm'">

                    <p class="mb-8">
                        Soll
                        <span class="font-bold">{{ selectedItem.name }}</span> gebucht werden?

                        <i class="fa fa-clock mr-2"></i>{{ date(start) }} {{ time(start) }} -
                        <span v-show="date(start) !== date(end)">
                            {{ date(end) }}
                        </span>
                        {{ time(end) }} Uhr

                    </p>

                </div>

                <template v-slot:action>
                    <button-primary v-show="page === 'confirm'" @click="create">
                        <template slot="icon"><i class="fa fa-save"></i></template>
                        Buchen
                    </button-primary>
                </template>

            </modal>
        </portal>
    </div>
</template>

<script>
import Modal from "../Modal";
import ButtonSecondary from "../UI/ButtonSecondary";
import ButtonPrimary from "../UI/ButtonPrimary";
import ButtonWhite from "../UI/ButtonWhite.vue";
import InputText from "../UI/InputText";
import SelectMenu from "../UI/SelectMenu";
import TailwindTable from "../UI/TailwindTable";
import ButtonLink from "../UI/ButtonLink";
import Loading from "../UI/Loading";
import UserSelect from "../UserSelect.vue";
import DateTimeRangeSelect from "../DateTimeRangeSelect.vue";
import ToggleGroup from "../UI/ToggleGroup.vue";
import TRichSelect from "vue-tailwind/dist/t-rich-select";

export default {
    name: "BookableBookingCreate",

    components: {
        TRichSelect,
        ButtonLink,
        ButtonPrimary,
        ButtonSecondary,
        ButtonWhite,
        DateTimeRangeSelect,
        InputText,
        Loading,
        Modal,
        SelectMenu,
        TailwindTable,
        ToggleGroup,
        UserSelect,
    },

    inject: ['can'],

    props: ['category'],

    data: (vm) => ({
        showModal: false,
        loading: false,
        comment: '',
        items: [],
        selectedItem: null,
        userId: null,
        range: {
            start: (new Date()).toISOString().substring(0, 10) + ' 06:00',
            end: (new Date()).toISOString().substring(0, 10) + ' 07:00',
        },
        timeSelectionMode: vm.category.time_selection_mode,
        hasOneBookingPerDay: false,
    }),

    watch: {
        range: {
            deep: true,
            handler() {
                this.search();
            },
        }
    },

    computed: {
        page() {
            if (this.selectedItem) {
                return 'confirm';
            }

            return 'overview';
        },
        start() {
            return this.range.start;
        },
        end() {
            return this.range.end;
        },
        startTime(){
            return this.range.start.substring(11, 16)
        },
        endTime(){
            return this.range.end.substring(11, 16)
        }
    },

    methods: {
        time(val) {
            return val.substring(11, 16);
        },
        date(val) {
            let [y, m, d] = val.substring(0, 10).split('-');
            return `${d}.${m}.${y}`;
        },
        open() {
            this.showModal = true;
            this.search();
        },
        search() {
            this.selectedItem = null;
            this.loading = true;
            this.$http.get(`/admin/bookable_bookings`, {
                    params: {
                        category: this.category.id,
                        start: this.range.start,
                        end: this.range.end,
                    },
                },
            )
                .then(response => {
                    this.loading = false;
                    this.items = response.data.data;
                })
                .catch(error => {
                    this.loading = false;
                    this.$root.$emit('error', error);
                });
        },
        create() {
            this.loading = true;
            this.$http.post('/admin/bookable_bookings', {
                start: this.range.start,
                end: this.range.end,
                comment: this.comment,
                bookable_id: this.selectedItem.id,
                user_id: this.userId,
                one_booking_per_day: this.hasOneBookingPerDay,
            }).then(() => {
                this.loading = false;
                this.showModal = false;
                this.$emit('created');
            }).catch(error => {
                this.loading = false;
                this.$root.$emit('error', error);
            })
        },
        getUsers(search) {
            return this.$http
                .get('/admin/users',{params: {search}})
                .then((data) => ({results: [{id:'guest', full_name:"Gästebuchung"},...data.data.data] }))
                .catch(error => {
                    this.$root.$emit('error', error);
                });
        },
    }

}
</script>

<style scoped>

</style>
